import { useCallback, useMemo } from 'react'
import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { useSelector } from 'react-redux'
import { AppStackParamList, RootStackParamList, VideoCallProduct } from '@src/navigation/types'
import { CoachesCoachingKind, EhrAppointmentKind, EhrAppointmentSchedulingMode } from '@src/types'
import { ehrAppointmentSchedulingInfoSelector } from '../../models/nutritionistHub.selectors'

type StressManagementSource = NonNullable<
  AppStackParamList['StressManagementIntroScreen']
>['source']

interface ScheduleCoachVideoCallOptions {
  coachingKind: CoachesCoachingKind
  onScheduleInitialMntAppointment?: (appointmentKind: EhrAppointmentKind) => void
  stressManagementSource?: StressManagementSource
}

interface ScheduleMntVideoCallOptions {
  mnt?: {
    bookingAvailable: boolean
    options: {
      appointmentKind: EhrAppointmentKind
      payAsYouGoProduct?: VideoCallProduct | null
      product?: VideoCallProduct | null
    }[]
    pendingAppointment?: { id: string } | null
    schedulingMode: EhrAppointmentSchedulingMode
  }
  navigation: Pick<StackNavigationProp<RootStackParamList>, 'navigate'>
  onScheduleInitialMntAppointment?: (appointmentKind: EhrAppointmentKind) => void
  navigateToGettingStartedForUndetermined?: boolean
}

type MntSchedulingInfo = NonNullable<ScheduleMntVideoCallOptions['mnt']>

const getInitialMntOption = (
  options?: MntSchedulingInfo['options'],
): MntSchedulingInfo['options'][number] | undefined =>
  options?.find(
    (option) =>
      option.appointmentKind === EhrAppointmentKind.Initial ||
      option.appointmentKind === EhrAppointmentKind.Initial_60,
  )

export const scheduleMntVideoCall = ({
  mnt,
  navigation,
  onScheduleInitialMntAppointment,
  navigateToGettingStartedForUndetermined = true,
}: ScheduleMntVideoCallOptions) => {
  const initialMntOption = getInitialMntOption(mnt?.options)
  const isUndeterminedMntMode = mnt?.schedulingMode === EhrAppointmentSchedulingMode.Undetermined

  if (isUndeterminedMntMode && navigateToGettingStartedForUndetermined) {
    navigation.navigate('VideoCallsGettingStartedScreen')
    return
  }

  if (initialMntOption) {
    onScheduleInitialMntAppointment?.(initialMntOption.appointmentKind)

    navigation.navigate('CallScheduling', {
      appointmentKind: initialMntOption.appointmentKind,
      allowBackNavigation: true,
      product: initialMntOption.payAsYouGoProduct,
      pendingAppointmentId: mnt?.pendingAppointment?.id,
    })
    return
  }

  const nextMntOption = mnt?.options[0]

  if (mnt?.bookingAvailable && nextMntOption) {
    navigation.navigate('CallScheduling', {
      allowBackNavigation: true,
      appointmentKind: nextMntOption.appointmentKind,
      product: nextMntOption.payAsYouGoProduct,
      pendingAppointmentId: mnt.pendingAppointment?.id,
    })
    return
  }

  navigation.navigate('VideoCallCheckout', {
    product: nextMntOption?.product || undefined,
  })
}

export const useScheduleCoachVideoCall = () => {
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>()
  const { mnt } = useSelector(ehrAppointmentSchedulingInfoSelector) || {}

  const initialMntOption = useMemo(() => getInitialMntOption(mnt?.options), [mnt])

  const isInitialMntKind = !!initialMntOption
  const isUndeterminedMntMode = mnt?.schedulingMode === EhrAppointmentSchedulingMode.Undetermined

  const scheduleCoachVideoCall = useCallback(
    ({
      coachingKind,
      onScheduleInitialMntAppointment,
      stressManagementSource = 'scheduler',
    }: ScheduleCoachVideoCallOptions) => {
      if (
        coachingKind === CoachesCoachingKind.Mnt ||
        coachingKind === CoachesCoachingKind.HealthCoaching
      ) {
        scheduleMntVideoCall({
          mnt,
          navigation,
          onScheduleInitialMntAppointment,
        })
        return
      }

      if (coachingKind === CoachesCoachingKind.StressManagement) {
        navigation.navigate('StressManagementIntroScreen', { source: stressManagementSource })
      }
    },
    [mnt, navigation],
  )

  return {
    initialMntOption,
    isInitialMntKind,
    isUndeterminedMntMode,
    scheduleCoachVideoCall,
  }
}
